/**
 * The way back from the Conversation page (TRA-1412).
 *
 * Every in-app link to `/conversations` carries router state naming the
 * page it left: `from` is that page's path plus search, so its filters and
 * an open request sheet come back with it, and `label` names it on the
 * back arrow ("Back to Cartage-1", "Back to trajectories"). A direct link,
 * a new tab or a reload has no state and gets the Trajectories fallback.
 */

import { useLocation } from 'react-router-dom'

export interface ConversationLinkState {
  /** Path and search of the page the link was on. */
  from: string
  /** The back arrow's text, e.g. "Back to trajectories". */
  label: string
}

export interface BackLink {
  to: string
  label: string
}

/** The link state for a page, from the page's current location. */
export function conversationLinkState(
  location: { pathname: string; search: string },
  pageName: string,
): ConversationLinkState {
  return { from: `${location.pathname}${location.search}`, label: `Back to ${pageName}` }
}

/** `conversationLinkState` for the page this component renders on. */
export function useConversationLinkState(pageName: string): ConversationLinkState {
  return conversationLinkState(useLocation(), pageName)
}

export function conversationPath(conversationId: string): string {
  return `/conversations?conversation_id=${encodeURIComponent(conversationId)}`
}

/**
 * A same-app path only: one leading slash. `//evil.com` would be read by
 * the router as a protocol-relative URL, and `https://…` is not a path.
 */
function isAppPath(from: unknown): from is string {
  return typeof from === 'string' && from.startsWith('/') && !from.startsWith('//')
}

/**
 * Where the Conversation page's back arrow goes. `state` is whatever the
 * router holds, which anyone can craft, so only a same-app `from` with a
 * label is trusted; everything else gets the Trajectories fallback, with
 * the dev-only vanity-org preview kept.
 */
export function backLink(state: unknown, vanityOrg: string | null): BackLink {
  if (typeof state === 'object' && state !== null && 'from' in state && 'label' in state) {
    const { from, label } = state
    if (isAppPath(from) && typeof label === 'string' && label !== '') return { to: from, label }
  }
  return {
    to: vanityOrg === null ? '/trajectories' : `/trajectories?vanity-org=${encodeURIComponent(vanityOrg)}`,
    label: 'Back to trajectories',
  }
}
